<template>
  <div class="destinatarios-email">
    <!-- Adicionar usuário do sistema -->
    <div class="row mb-3 align-items-end">
      <div class="col-md-6">
        <strong class="d-block mb-2">Adicionar usuário do sistema</strong>
        <SGPFormAutocompleteComponent
          name="adicionar_usuario"
          :listaDeOptions="usuariosDisponiveis"
          optionLabel="rotulo"
          optionValue="id"
          placeholder="Buscar por nome ou e-mail"
          v-model="usuarioSelecionado"
          :limpavel="true"
          :loadingProp="loadingUsuarios"
          @change="adicionarUsuario"
        />
      </div>

      <!-- Adicionar destinatário livre -->
      <div class="col-md-6">
        <strong class="d-block mb-2">Adicionar destinatário livre</strong>
        <div class="row no-gutters align-items-start">
          <div class="col pr-1">
            <SGPFormInputComponent
              type="text"
              name="dest_livre_nome"
              v-model="livraNome"
              placeholder="Nome (opcional)"
            />
          </div>
          <div class="col pr-1">
            <SGPFormInputComponent
              type="text"
              name="dest_livre_email"
              v-model="livreEmail"
              placeholder="email@exemplo.com"
              :error="erroLivreEmail"
            />
          </div>
          <div class="col-auto pt-1">
            <button
              type="button"
              class="v-btn v-btn--has-bg v-btn--rounded theme--light v-size--small primary text-capitalize"
              :disabled="!livreEmail"
              @click="adicionarLivre"
            >
              + Add
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Lista -->
    <strong class="d-block mb-2">Destinatários configurados</strong>
    <p v-if="!lista.length" class="text-muted small mb-2">
      Nenhum destinatário configurado.
    </p>

    <ul class="list-unstyled destinatarios-lista">
      <li
        v-for="(dest, index) in lista"
        :key="`dest-${index}`"
        class="destinatario-item d-flex align-items-center justify-content-between"
      >
        <div>
          <SGPIconComponent
            :icon="dest.user_id ? 'mdi-account' : 'mdi-email-outline'"
            class="mr-2"
          />
          <span>{{ rotulo(dest) }}</span>
          <small v-if="dest.user_id" class="text-muted ml-2">Usuário do sistema</small>
          <small v-else class="text-muted ml-2">Destinatário livre</small>
        </div>
        <button
          type="button"
          class="v-btn v-btn--text v-btn--icon theme--light v-size--small"
          title="Remover"
          @click="remover(index)"
        >
          <SGPIconComponent icon="mdi-delete" />
        </button>
      </li>
    </ul>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { formatarDestinatario } from '../helpers/destinatarios'

const VALIDADOR_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default {
  name: 'DestinatariosEmailComponent',

  props: {
    value: {
      type: Array,
      default: () => []
    }
  },

  data () {
    return {
      lista: this.normalizar(this.value),
      usuarioSelecionado: null,
      livraNome: '',
      livreEmail: '',
      erroLivreEmail: '',
      loadingUsuarios: false
    }
  },

  computed: {
    ...mapGetters('usuarios', ['listaDeUsuarios']),

    usuariosDisponiveis () {
      const idsSelecionados = new Set(
        this.lista.filter(d => d.user_id).map(d => Number(d.user_id))
      )
      return (this.listaDeUsuarios || [])
        .filter(u => !idsSelecionados.has(Number(u.id)))
        .map(u => ({
          id: u.id,
          rotulo: u.email ? `${u.name} (${u.email})` : u.name
        }))
    }
  },

  watch: {
    value (novo) {
      const normalizado = this.normalizar(novo)
      if (JSON.stringify(normalizado) !== JSON.stringify(this.lista)) {
        this.lista = normalizado
      }
    }
  },

  async created () {
    if (!this.listaDeUsuarios || this.listaDeUsuarios.length === 0) {
      this.loadingUsuarios = true
      try {
        await this.listarUsuarios()
      } finally {
        this.loadingUsuarios = false
      }
    }
  },

  methods: {
    ...mapActions('usuarios', ['listarUsuarios']),

    normalizar (input) {
      if (!Array.isArray(input)) return []
      return input.map(d => ({
        user_id: d.user_id ?? null,
        name: d.name ?? null,
        email: d.email ?? null,
        user: d.user ?? null
      }))
    },

    emitir () {
      this.$emit('input', this.lista.map(d => ({ ...d })))
    },

    rotulo (dest) {
      return formatarDestinatario(dest)
    },

    adicionarUsuario (userId) {
      if (!userId) return
      const u = (this.listaDeUsuarios || []).find(x => Number(x.id) === Number(userId))
      if (!u) return
      if (this.lista.some(d => d.user_id && Number(d.user_id) === Number(userId))) {
        this.usuarioSelecionado = null
        return
      }
      this.lista.push({
        user_id: Number(userId),
        name: null,
        email: null,
        user: { id: u.id, name: u.name, email: u.email }
      })
      this.usuarioSelecionado = null
      this.emitir()
    },

    adicionarLivre () {
      const email = (this.livreEmail || '').trim()
      if (!email) {
        this.erroLivreEmail = 'Informe um e-mail.'
        return
      }
      if (!VALIDADOR_EMAIL.test(email)) {
        this.erroLivreEmail = 'E-mail inválido.'
        return
      }
      if (this.lista.some(d => !d.user_id && (d.email || '').toLowerCase() === email.toLowerCase())) {
        this.erroLivreEmail = 'Este e-mail já foi adicionado.'
        return
      }
      this.lista.push({
        user_id: null,
        name: (this.livraNome || '').trim() || null,
        email,
        user: null
      })
      this.livraNome = ''
      this.livreEmail = ''
      this.erroLivreEmail = ''
      this.emitir()
    },

    remover (index) {
      this.lista.splice(index, 1)
      this.emitir()
    }
  }
}
</script>

<style scoped>
.destinatarios-lista {
  margin: 0;
  padding: 0;
}

.destinatario-item {
  background-color: #fafafa;
  border: 1px solid #eee;
  border-radius: 4px;
  padding: 8px 12px;
  margin-bottom: 6px;
}
</style>
